import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSafetyTrainingContext } from '../../contexts/SafetyTrainingContext';
import { useUserContext } from '../../contexts/UserContext';
import { LIBRARY_TEMPLATE_COMPANY_ID } from '../../utils/constants';
import { listAllLibraryPlanTemplates } from '../../graphql/resourceLibraryQueries';
import {
    PlanCategory,
    GoverningStandard,
    PlanStatus,
    DEFAULT_WORKING_DAYS,
} from '../../types/trainingTypes';
import { validatePlanCategories, calculatePlanDurationStats } from '../../utils/trainingUtils';
import { QuizBuilderModal } from './QuizBuilderModal';
import { DeletePlanConfirmModal } from './DeletePlanConfirmModal';
import { BackButton } from '../ui';
import '../../css/PlanBuilderPage.css';

interface PlanBuilderPageProps {
    onBack: () => void;
    editPlanId?: string | null;
    templateMode?: boolean;
    onImportFromLibrary?: () => void;
}

const STEP_LABELS = [
    'Plan Name & Standard',
    'Minimum Time',
    'Categories & Targets',
    'Schedule Duration',
    'Review & Save',
];

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const GOVERNING_STANDARDS: { value: GoverningStandard; label: string; desc: string }[] = [
    { value: 'MSHA', label: 'MSHA', desc: 'Mine Safety & Health Administration' },
    { value: 'OSHA', label: 'OSHA', desc: 'Occupational Safety & Health Administration' },
    { value: 'COMPANY', label: 'Company', desc: 'Internal company standard' },
];

const CYCLE_PRESETS: { days: number; label: string }[] = [
    { days: 90, label: 'Quarterly' },
    { days: 180, label: 'Semi-Annual' },
    { days: 365, label: 'Annual' },
];

function generateId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `cat-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

const PlanBuilderPage = ({ onBack, editPlanId, templateMode = false, onImportFromLibrary }: PlanBuilderPageProps) => {
    const { plans, createPlan, updatePlan, deletePlan } = useSafetyTrainingContext();
    const { companyUser, companyOwner } = useUserContext();
    const userCompanyId = (companyUser?.companyId as string) || companyOwner?.companyId || '';
    const companyId = templateMode ? LIBRARY_TEMPLATE_COMPANY_ID : userCompanyId;

    // Check for available library plans
    const [hasLibraryPlans, setHasLibraryPlans] = useState(false);
    useEffect(() => {
        if (!companyId) return;
        listAllLibraryPlanTemplates().then((plans: any[]) => {
            setHasLibraryPlans(plans.some(p => p.sharedWithCompanyIds?.includes(companyId) && p.status === 'ACTIVE'));
        }).catch(() => { /* silently fail */ });
    }, [companyId]);

    // Wizard state
    const [currentStep, setCurrentStep] = useState(1);
    const [isSaving, setIsSaving] = useState(false);

    // Step 1 — Plan Name & Standard
    const [planName, setPlanName] = useState('');
    const [governingStandard, setGoverningStandard] = useState<GoverningStandard | ''>('');

    // Step 2 — Minimum Time Required
    const [minimumTimeRequired, setMinimumTimeRequired] = useState<number>(0);

    // Step 3 — Categories & Time Targets
    const [categories, setCategories] = useState<PlanCategory[]>([
        { id: generateId(), name: '', targetMinutes: 0 },
    ]);

    // Step 4 — Schedule Duration
    const [cycleDuration, setCycleDuration] = useState<number>(365);
    const [selectedPreset, setSelectedPreset] = useState<number | 'custom'>(365);
    const [customCycleDays, setCustomCycleDays] = useState<string>('');
    const [workingDays, setWorkingDays] = useState<number[]>([...DEFAULT_WORKING_DAYS]);

    // Quiz Builder Modal state
    const [quizBuilderCategory, setQuizBuilderCategory] = useState<PlanCategory | null>(null);

    // Delete confirmation modal state
    const [deletingPlan, setDeletingPlan] = useState<{ id: string; name: string } | null>(null);

    // Pre-populate when editing an existing plan
    useEffect(() => {
        if (!editPlanId) return;
        const plan = plans.find(p => p.id === editPlanId);
        if (!plan) return;

        setPlanName(plan.name);
        setGoverningStandard(plan.governingStandard);
        setMinimumTimeRequired(plan.minimumTimeRequired);
        setCycleDuration(plan.cycleDuration);
        setWorkingDays(plan.workingDays ?? [...DEFAULT_WORKING_DAYS]);

        // Set preset or custom
        const presetMatch = CYCLE_PRESETS.find(p => p.days === plan.cycleDuration);
        if (presetMatch) {
            setSelectedPreset(presetMatch.days);
        } else {
            setSelectedPreset('custom');
            setCustomCycleDays(String(plan.cycleDuration));
        }

        try {
            const parsed = JSON.parse(plan.categories) as PlanCategory[];
            if (parsed.length > 0) setCategories(parsed);
        } catch {
            // keep default
        }
    }, [editPlanId, plans]);

    // Duration stats (used in steps 2 and 4)
    const durationStats = useMemo(
        () => calculatePlanDurationStats(cycleDuration, minimumTimeRequired, workingDays),
        [cycleDuration, minimumTimeRequired, workingDays]
    );

    const totalAssigned = useMemo(
        () => categories.reduce((sum, c) => sum + (c.targetMinutes || 0), 0),
        [categories]
    );

    const difference = totalAssigned - minimumTimeRequired;

    const categoryValidation = useMemo(
        () => validatePlanCategories(categories, minimumTimeRequired),
        [categories, minimumTimeRequired]
    );

    // -----------------------------------------------------------------------
    // Step navigation
    // -----------------------------------------------------------------------
    const canGoNext = useCallback((): boolean => {
        switch (currentStep) {
            case 1:
                return planName.trim().length > 0 && governingStandard !== '';
            case 2:
                return minimumTimeRequired > 0;
            case 3:
                return categories.length > 0;
            case 4:
                return cycleDuration > 0;
            default:
                return false;
        }
    }, [currentStep, planName, governingStandard, minimumTimeRequired, categories, cycleDuration]);

    const goNext = () => {
        if (currentStep < 5 && canGoNext()) setCurrentStep(currentStep + 1);
    };

    const goBack = () => {
        if (currentStep > 1) setCurrentStep(currentStep - 1);
    };

    // -----------------------------------------------------------------------
    // Category management
    // -----------------------------------------------------------------------
    const addCategory = () => {
        setCategories(prev => [...prev, { id: generateId(), name: '', targetMinutes: 0 }]);
    };

    const updateCategory = (id: string, field: keyof PlanCategory, value: string | number | boolean) => {
        setCategories(prev =>
            prev.map(c => (c.id === id ? { ...c, [field]: value } : c))
        );
    };

    const removeCategory = (id: string) => {
        setCategories(prev => prev.filter(c => c.id !== id));
    };

    // -----------------------------------------------------------------------
    // Working days toggle
    // -----------------------------------------------------------------------
    const toggleWorkingDay = (day: number) => {
        setWorkingDays(prev =>
            prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day].sort()
        );
    };

    // -----------------------------------------------------------------------
    // Cycle preset selection
    // -----------------------------------------------------------------------
    const selectPreset = (days: number) => {
        setSelectedPreset(days);
        setCycleDuration(days);
        setCustomCycleDays('');
    };

    const selectCustom = () => {
        setSelectedPreset('custom');
        const val = parseInt(customCycleDays, 10);
        if (val > 0) setCycleDuration(val);
    };

    const handleCustomCycleChange = (val: string) => {
        setCustomCycleDays(val);
        const num = parseInt(val, 10);
        if (num > 0) setCycleDuration(num);
    };

    // -----------------------------------------------------------------------
    // Save handlers
    // -----------------------------------------------------------------------
    const handleSave = async (status: PlanStatus) => {
        setIsSaving(true);
        try {
            const categoriesJson = JSON.stringify(categories);
            if (editPlanId) {
                await updatePlan({
                    id: editPlanId,
                    name: planName.trim(),
                    governingStandard,
                    minimumTimeRequired,
                    cycleDuration,
                    status,
                    categories: categoriesJson,
                    workingDays,
                });
            } else {
                await createPlan({
                    companyId,
                    name: planName.trim(),
                    governingStandard,
                    minimumTimeRequired,
                    cycleDuration,
                    status,
                    categories: categoriesJson,
                    workingDays,
                });
            }
            onBack();
        } catch (err) {
            console.error('Error saving plan:', err);
        } finally {
            setIsSaving(false);
        }
    };

    // -----------------------------------------------------------------------
    // Render helpers
    // -----------------------------------------------------------------------
    const standardLabel = GOVERNING_STANDARDS.find(s => s.value === governingStandard)?.label ?? '—';
    const workingDayNames = workingDays.sort((a, b) => a - b).map(d => DAY_NAMES[d]).join(', ');

    return (
        <div className={`pb-container${isSaving ? ' pb-saving-overlay' : ''}`}>
            <BackButton onClick={onBack} buttonText="Back to Safety & Training Home" />

            {/* Stepper */}
            <div className="pb-stepper">
                {STEP_LABELS.map((label, idx) => {
                    const stepNum = idx + 1;
                    const isCompleted = stepNum < currentStep;
                    const isActive = stepNum === currentStep;
                    return (
                        <div key={stepNum} style={{ display: 'flex', alignItems: 'center' }}>
                            {idx > 0 && (
                                <div className={`pb-step-connector${isCompleted ? ' completed' : ''}`} />
                            )}
                            <div className={`pb-step${isActive ? ' active' : ''}${isCompleted ? ' completed' : ''}`}>
                                <div className="pb-step-circle">
                                    {isCompleted ? '✓' : stepNum}
                                </div>
                                <span>{label}</span>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="pb-layout">
                {/* Sidebar */}
                <div className="pb-sidebar">
                    <div className="pb-sidebar-section">
                        <h3>Training Plans</h3>
                        <div className="pb-plan-list">
                            {plans.map(plan => (
                                <div
                                    key={plan.id}
                                    className={`pb-plan-item${editPlanId === plan.id ? ' selected' : ''}`}
                                    onClick={() => onBack()}
                                >
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <span className="pb-plan-item-name">{plan.name}</span>
                                        <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginTop: 2 }}>
                                            <span className={`pb-plan-status-badge ${plan.status.toLowerCase()}`}>
                                                {plan.status === 'ACTIVE' ? 'Active' : plan.status === 'DRAFT' ? 'Draft' : 'Completed'}
                                            </span>
                                            {plan.sourceTemplateId && (
                                                <span style={{ fontSize: 9, background: '#ede9fe', color: '#6d28d9', padding: '1px 5px', borderRadius: 4, fontWeight: 600 }}>
                                                    📥 Library
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {plan.status === 'DRAFT' && (
                                        <button
                                            className="pb-plan-delete-btn"
                                            title="Delete plan"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setDeletingPlan({ id: plan.id, name: plan.name });
                                            }}
                                        >
                                            🗑
                                        </button>
                                    )}
                                </div>
                            ))}
                            {plans.length === 0 && (
                                <div style={{ fontSize: 12, color: '#94a3b8', padding: '8px 0', lineHeight: 1.4 }}>
                                    No plans yet. Plans you create will appear here.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Import from Library section — hidden in template mode */}
                    {!templateMode && (
                        <div className="pb-sidebar-section">
                            <h3>Import from Library</h3>
                            <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.4, marginBottom: 8 }}>
                                {hasLibraryPlans
                                    ? 'Import a pre-built training plan from the Zorva library. You\'ll get a full editable copy with all topics and categories.'
                                    : 'No library plans are available for your company yet. Contact your Zorva administrator to get plans assigned.'}
                            </div>
                            <button
                                className="pb-new-plan-btn"
                                style={{ background: hasLibraryPlans ? '#3b82f6' : '#94a3b8', cursor: hasLibraryPlans ? 'pointer' : 'not-allowed' }}
                                disabled={!hasLibraryPlans}
                                onClick={() => { if (onImportFromLibrary) onImportFromLibrary(); }}
                            >
                                📥 Browse Library Plans
                            </button>
                        </div>
                    )}

                    <div className="pb-sidebar-section">
                        <h3>Building a Plan</h3>
                        <ol className="pb-guide-list">
                            <li>
                                <span className="pb-guide-number">1</span>
                                Name your plan and choose a governing standard
                            </li>
                            <li>
                                <span className="pb-guide-number">2</span>
                                Set the minimum training time required
                            </li>
                            <li>
                                <span className="pb-guide-number">3</span>
                                Create categories and assign time targets
                            </li>
                            <li>
                                <span className="pb-guide-number">4</span>
                                Choose the training cycle duration
                            </li>
                            <li>
                                <span className="pb-guide-number">5</span>
                                Review and save your plan
                            </li>
                        </ol>
                    </div>
                </div>

                {/* Main content */}
                <div className="pb-main">
                    {currentStep === 1 && renderStep1()}
                    {currentStep === 2 && renderStep2()}
                    {currentStep === 3 && renderStep3()}
                    {currentStep === 4 && renderStep4()}
                    {currentStep === 5 && renderStep5()}
                </div>
            </div>

            {/* Quiz Builder Modal */}
            {quizBuilderCategory && (
                <QuizBuilderModal
                    visible={!!quizBuilderCategory}
                    onDismiss={() => setQuizBuilderCategory(null)}
                    category={quizBuilderCategory}
                    companyId={companyId}
                    planId={editPlanId || ''}
                    onQuizSaved={(categoryId, quizFormId) => {
                        setCategories(prev =>
                            prev.map(c => c.id === categoryId ? { ...c, quizFormId } : c)
                        );
                        setQuizBuilderCategory(null);
                    }}
                />
            )}

            {/* Delete Plan Confirmation Modal */}
            <DeletePlanConfirmModal
                isOpen={!!deletingPlan}
                planName={deletingPlan?.name || ''}
                onClose={() => setDeletingPlan(null)}
                onConfirm={async () => {
                    if (deletingPlan) {
                        await deletePlan(deletingPlan.id);
                        setDeletingPlan(null);
                    }
                }}
            />
        </div>
    );

    // ===================================================================
    // Step 1 — Plan Name & Standard
    // ===================================================================
    function renderStep1() {
        return (
            <>
                <h2 className="pb-step-title">Plan Name & Standard</h2>
                <p className="pb-step-subtitle">Name your training plan and select the governing standard it satisfies.</p>

                <div className="pb-field">
                    <label htmlFor="pb-plan-name">Plan Name *</label>
                    <input
                        id="pb-plan-name"
                        type="text"
                        placeholder="e.g. MSHA 8-Hour Refresher"
                        value={planName}
                        onChange={e => setPlanName(e.target.value)}
                    />
                </div>

                <div className="pb-field">
                    <label>Governing Standard *</label>
                    <div className="pb-standard-cards">
                        {GOVERNING_STANDARDS.map(std => (
                            <div
                                key={std.value}
                                className={`pb-standard-card${governingStandard === std.value ? ' selected' : ''}`}
                                onClick={() => setGoverningStandard(std.value)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setGoverningStandard(std.value); }}
                                aria-pressed={governingStandard === std.value}
                            >
                                <div className="pb-standard-card-label">{std.label}</div>
                                <div className="pb-standard-card-desc">{std.desc}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="pb-actions">
                    <button className="pb-btn-primary" disabled={!canGoNext()} onClick={goNext}>
                        Next — Set Minimum Time
                    </button>
                </div>
            </>
        );
    }

    // ===================================================================
    // Step 2 — Minimum Time Required
    // ===================================================================
    function renderStep2() {
        return (
            <>
                <h2 className="pb-step-title">Minimum Time Required</h2>
                <p className="pb-step-subtitle">
                    The total minutes of training this plan must deliver across all categories.
                </p>

                <div className="pb-field">
                    <label htmlFor="pb-min-time">Minimum Time Required (minutes)</label>
                    <input
                        id="pb-min-time"
                        type="number"
                        min={0}
                        placeholder="e.g. 480"
                        value={minimumTimeRequired || ''}
                        onChange={e => setMinimumTimeRequired(Math.max(0, parseInt(e.target.value, 10) || 0))}
                    />
                    <div className="pb-helper-text">MSHA 8-hour refresher = 480 minutes</div>
                </div>

                {minimumTimeRequired > 0 && (
                    <div className="pb-calc-info">
                        <div className="pb-calc-item">
                            <div className="pb-calc-value">{durationStats.equivalentHours.toFixed(1)}</div>
                            <div className="pb-calc-label">Equivalent Hours</div>
                        </div>
                        <div className="pb-calc-item">
                            <div className="pb-calc-value">{durationStats.avgMinutesPerWorkingDay.toFixed(1)}</div>
                            <div className="pb-calc-label">Avg Min / Working Day</div>
                        </div>
                        <div className="pb-calc-item">
                            <div className="pb-calc-value">{durationStats.estimatedTopics}</div>
                            <div className="pb-calc-label">Est. Topics (5 min avg)</div>
                        </div>
                    </div>
                )}

                <div className="pb-actions">
                    <button className="pb-btn-secondary" onClick={goBack}>Back</button>
                    <button className="pb-btn-primary" disabled={!canGoNext()} onClick={goNext}>
                        Next — Define Categories
                    </button>
                </div>
            </>
        );
    }

    // ===================================================================
    // Step 3 — Categories & Time Targets
    // ===================================================================
    function renderStep3() {
        return (
            <>
                <h2 className="pb-step-title">Categories & Time Targets</h2>
                <p className="pb-step-subtitle">
                    Create the categories for this plan and assign a required time target to each.
                </p>

                <table className="pb-categories-table">
                    <thead>
                        <tr>
                            <th style={{ width: '35%' }}>Category Name</th>
                            <th style={{ width: '20%' }}>Target (min)</th>
                            <th style={{ width: '15%' }}>Quiz</th>
                            <th style={{ width: '20%' }}></th>
                            <th style={{ width: '10%' }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {categories.map(cat => (
                            <tr key={cat.id}>
                                <td>
                                    <input
                                        type="text"
                                        placeholder="e.g. Electrical Safety"
                                        value={cat.name}
                                        onChange={e => updateCategory(cat.id, 'name', e.target.value)}
                                    />
                                </td>
                                <td>
                                    <input
                                        type="number"
                                        min={0}
                                        placeholder="60"
                                        value={cat.targetMinutes || ''}
                                        onChange={e =>
                                            updateCategory(cat.id, 'targetMinutes', Math.max(0, parseInt(e.target.value, 10) || 0))
                                        }
                                    />
                                </td>
                                <td>
                                    <label className="pb-quiz-toggle">
                                        <input
                                            type="checkbox"
                                            checked={cat.requiresQuiz || false}
                                            onChange={e => updateCategory(cat.id, 'requiresQuiz', e.target.checked)}
                                        />
                                        <span className="pb-quiz-toggle-label">Required</span>
                                    </label>
                                </td>
                                <td>
                                    {cat.requiresQuiz && (
                                        <button
                                            className="pb-quiz-edit-btn"
                                            onClick={() => setQuizBuilderCategory(cat)}
                                        >
                                            {cat.quizFormId ? '✎ Edit Quiz' : '+ Create Quiz'}
                                        </button>
                                    )}
                                </td>
                                <td>
                                    <button
                                        className="pb-cat-delete-btn"
                                        onClick={() => removeCategory(cat.id)}
                                        title="Remove category"
                                        aria-label={`Remove category ${cat.name || 'unnamed'}`}
                                    >
                                        ✕
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <button className="pb-add-cat-btn" onClick={addCategory}>
                    + Add Category
                </button>

                {/* Validation summary */}
                <div className="pb-validation-summary">
                    <div className="pb-validation-item">
                        <div className="pb-val-label">Total Assigned</div>
                        <div className="pb-val-value">{totalAssigned} min</div>
                    </div>
                    <div className="pb-validation-item">
                        <div className="pb-val-label">Plan Minimum</div>
                        <div className="pb-val-value">{minimumTimeRequired} min</div>
                    </div>
                    <div className="pb-validation-item">
                        <div className="pb-val-label">Difference</div>
                        <div className="pb-val-value">{difference >= 0 ? `+${difference}` : difference} min</div>
                    </div>
                    {difference < 0 ? (
                        <div className="pb-validation-warning">
                            ⚠ {Math.abs(difference)} min short of requirement
                        </div>
                    ) : categoryValidation.valid ? (
                        <div className="pb-validation-ok">
                            ✓ Meets requirement
                        </div>
                    ) : (
                        <div className="pb-validation-warning">
                            ⚠ {categoryValidation.errors[0]}
                        </div>
                    )}
                </div>

                <div className="pb-actions">
                    <button className="pb-btn-secondary" onClick={goBack}>Back</button>
                    <button className="pb-btn-primary" disabled={!canGoNext()} onClick={goNext}>
                        Next — Set Duration
                    </button>
                </div>
            </>
        );
    }

    // ===================================================================
    // Step 4 — Schedule Duration
    // ===================================================================
    function renderStep4() {
        return (
            <>
                <h2 className="pb-step-title">Schedule Duration</h2>
                <p className="pb-step-subtitle">
                    How many days should this plan's training cycle span?
                </p>

                <div className="pb-cycle-presets">
                    {CYCLE_PRESETS.map(preset => (
                        <div
                            key={preset.days}
                            className={`pb-cycle-preset${selectedPreset === preset.days ? ' selected' : ''}`}
                            onClick={() => selectPreset(preset.days)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') selectPreset(preset.days); }}
                            aria-pressed={selectedPreset === preset.days}
                        >
                            <div className="pb-cycle-preset-days">{preset.days} days</div>
                            <div className="pb-cycle-preset-label">{preset.label}</div>
                        </div>
                    ))}
                    <div
                        className={`pb-cycle-preset${selectedPreset === 'custom' ? ' selected' : ''}`}
                        onClick={selectCustom}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') selectCustom(); }}
                        aria-pressed={selectedPreset === 'custom'}
                    >
                        <div className="pb-cycle-preset-days">Custom</div>
                        <div className="pb-cycle-preset-label">Enter days</div>
                    </div>
                </div>

                {selectedPreset === 'custom' && (
                    <div className="pb-custom-cycle-input">
                        <input
                            type="number"
                            min={1}
                            placeholder="Days"
                            value={customCycleDays}
                            onChange={e => handleCustomCycleChange(e.target.value)}
                        />
                        <span>days</span>
                    </div>
                )}

                {/* Calculated stats */}
                <div className="pb-calc-info">
                    <div className="pb-calc-item">
                        <div className="pb-calc-value">{durationStats.workingDaysCount}</div>
                        <div className="pb-calc-label">Working Days</div>
                    </div>
                    <div className="pb-calc-item">
                        <div className="pb-calc-value">{durationStats.estimatedTopics}</div>
                        <div className="pb-calc-label">Topics Needed (min)</div>
                    </div>
                    <div className="pb-calc-item">
                        <div className="pb-calc-value">{durationStats.workingDaysCount}</div>
                        <div className="pb-calc-label">Topic Slots</div>
                    </div>
                </div>

                {/* Working Days configuration */}
                <div className="pb-working-days">
                    <div className="pb-working-days-label">Working Days</div>
                    <div className="pb-day-checkboxes">
                        {DAY_NAMES.map((name, idx) => (
                            <label
                                key={idx}
                                className={`pb-day-checkbox${workingDays.includes(idx) ? ' checked' : ''}`}
                            >
                                <input
                                    type="checkbox"
                                    checked={workingDays.includes(idx)}
                                    onChange={() => toggleWorkingDay(idx)}
                                />
                                {name}
                            </label>
                        ))}
                    </div>
                </div>

                <div className="pb-cycle-note">
                    When a plan is active, the same topic is reassigned exactly one cycle later — so the schedule always stays {cycleDuration} days ahead.
                </div>

                <div className="pb-actions">
                    <button className="pb-btn-secondary" onClick={goBack}>Back</button>
                    <button className="pb-btn-primary" disabled={!canGoNext()} onClick={goNext}>
                        Review Plan
                    </button>
                </div>
            </>
        );
    }

    // ===================================================================
    // Step 5 — Review & Save
    // ===================================================================
    function renderStep5() {
        return (
            <>
                <h2 className="pb-step-title">Review & Save</h2>
                <p className="pb-step-subtitle">Review your plan details before saving.</p>

                <div className="pb-review-section">
                    <h3>Plan Summary</h3>
                    <div className="pb-review-grid">
                        <div className="pb-review-item">
                            <span className="pb-review-item-label">Plan Name</span>
                            <span className="pb-review-item-value">{planName}</span>
                        </div>
                        <div className="pb-review-item">
                            <span className="pb-review-item-label">Governing Standard</span>
                            <span className="pb-review-item-value">{standardLabel}</span>
                        </div>
                        <div className="pb-review-item">
                            <span className="pb-review-item-label">Minimum Time Required</span>
                            <span className="pb-review-item-value">{minimumTimeRequired} min ({durationStats.equivalentHours.toFixed(1)} hrs)</span>
                        </div>
                        <div className="pb-review-item">
                            <span className="pb-review-item-label">Schedule Duration</span>
                            <span className="pb-review-item-value">{cycleDuration} days</span>
                        </div>
                        <div className="pb-review-item">
                            <span className="pb-review-item-label">Working Days</span>
                            <span className="pb-review-item-value">{workingDayNames}</span>
                        </div>
                    </div>
                </div>

                <div className="pb-review-section">
                    <h3>Categories & Time Targets</h3>
                    <table className="pb-review-table">
                        <thead>
                            <tr>
                                <th>Category</th>
                                <th>Target (min)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {categories.map(cat => (
                                <tr key={cat.id}>
                                    <td>{cat.name || '(unnamed)'}</td>
                                    <td>{cat.targetMinutes} min</td>
                                </tr>
                            ))}
                            <tr style={{ fontWeight: 600 }}>
                                <td>Total</td>
                                <td>{totalAssigned} min</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div className="pb-review-callout">
                    Next step: After saving, go to <strong>Create Schedule</strong> to assign topics to dates across the plan duration.
                </div>

                <div className="pb-actions">
                    <button className="pb-btn-secondary" onClick={goBack}>Back</button>
                    <button className="pb-btn-primary" onClick={() => handleSave('DRAFT')}>
                        Save Plan
                    </button>
                </div>
            </>
        );
    }
};

export default PlanBuilderPage;
